/**
 * Employee Tasks React Query Hooks
 *
 * Custom hooks for employee task assignment operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeTasksApi } from '../../lib/employeeTasksApi';
import { employeeKeys } from './useEmployees';

// Query keys for employee tasks
export const employeeTaskKeys = {
  all: (employeeId: number) => [...employeeKeys.detail(employeeId), 'tasks'] as const,
  assigned: (employeeId: number) => [...employeeTaskKeys.all(employeeId), 'assigned'] as const,
  available: (employeeId: number) => [...employeeTaskKeys.all(employeeId), 'available'] as const,
};

/**
 * Hook to fetch assigned tasks for an employee
 */
export function useEmployeeAssignedTasks(employeeId: number | undefined) {
  return useQuery({
    queryKey: employeeTaskKeys.assigned(employeeId!),
    queryFn: () => employeeTasksApi.getAssignedTasks(employeeId!),
    enabled: !!employeeId,
  });
}

/**
 * Hook to fetch available tasks for assignment
 */
export function useEmployeeAvailableTasks(employeeId: number | undefined) {
  return useQuery({
    queryKey: employeeTaskKeys.available(employeeId!),
    queryFn: () => employeeTasksApi.getAvailableTasks(employeeId!),
    enabled: !!employeeId,
  });
}

/**
 * Hook to assign task to employee
 */
export function useAssignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, taskId }: { employeeId: number; taskId: number }) =>
      employeeTasksApi.assignTask(employeeId, taskId),
    onSuccess: (_, { employeeId }) => {
      // Invalidate both assigned and available tasks
      queryClient.invalidateQueries({ queryKey: employeeTaskKeys.assigned(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeTaskKeys.available(employeeId) });
      // Also invalidate employee detail since it includes task count
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
    },
  });
}

/**
 * Hook to remove task assignment
 */
export function useRemoveTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, taskId }: { employeeId: number; taskId: number }) =>
      employeeTasksApi.removeTask(employeeId, taskId),
    onSuccess: (_, { employeeId }) => {
      // Invalidate both assigned and available tasks
      queryClient.invalidateQueries({ queryKey: employeeTaskKeys.assigned(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeTaskKeys.available(employeeId) });
      // Also invalidate employee detail since it includes task count
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
    },
  });
}

/**
 * Hook for bulk task assignment
 */
export function useBulkAssignTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, taskIds }: { employeeId: number; taskIds: number[] }) =>
      employeeTasksApi.bulkReplace(employeeId, taskIds),
    onSuccess: (_, { employeeId }) => {
      // Invalidate all task-related queries
      queryClient.invalidateQueries({ queryKey: employeeTaskKeys.all(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
    },
  });
}
